import { Injectable } from "@angular/core";
import { DbDaoBase } from "../../../../../gyzmo-commons/dao/db/base/db.dao.base";
import { isNullOrEmpty } from "../../../../../gyzmo-commons/helpers/null.helper";
import { AppSqlProvider } from "../../../../../gyzmo-commons/persistence/app.sql.provider";
import { LoggerService } from "../../../../../gyzmo-commons/services/logs/logger.service";
import { DamageCategory } from "../../../../models/inspection/common/damageCategory.model";
import { DamageValuesDbDao } from "./damageValues.db.dao";

@Injectable({
    providedIn: "root",
})
export class DamageCategoryDbDao extends DbDaoBase<DamageCategory> {
    constructor(
        logger: LoggerService,
        private sqlProvider: AppSqlProvider,
        private damageValuesDbDao: DamageValuesDbDao) {
        super(logger);
    }

    public getList(hydrate: boolean = false): Promise<DamageCategory[]> {
        const selectQuery = `SELECT * FROM ${ DamageCategory.TABLENAME };`;
        return this.startListQuery(selectQuery, hydrate);
    }

    public async createIndexes(): Promise<void> {
        // Implémentation des index si nécessaire
    }

    public createTable(): Promise<void> {
        const query = `CREATE TABLE IF NOT EXISTS ${ DamageCategory.TABLENAME } (
            id TEXT PRIMARY KEY,
            wording TEXT
        );`;
        return this.sqlProvider.query(query, [])
            .then(async () => {
                await this.createIndexes();
            })
            .catch(reason => {
                this.logSqlError(reason);
                return null;
            });
    }

    delete(id: string): Promise<any> {
        const deleteQuery = `DELETE FROM ${ DamageCategory.TABLENAME } WHERE id = ?;`;
        return this.sqlProvider.query(deleteQuery, [id]);
    }

    deleteAll(): Promise<any> {
        const deleteAllQuery = `DELETE FROM ${ DamageCategory.TABLENAME };`;
        return this.sqlProvider.query(deleteAllQuery, []);
    }

    public get(id: string, hydrate: boolean = false): Promise<DamageCategory> {
        if (isNullOrEmpty(id)) {
            return Promise.resolve(null);
        }
        const selectQuery = `SELECT * FROM ${ DamageCategory.TABLENAME } WHERE id = ?;`;
        return this.sqlProvider.query(selectQuery, [id])
            .then(data => {
                if (data.rows.length <= 0) {
                    return null;
                }
                const damageCategory = this.rowToModel(data.rows[0]);
                const hydratationPromises: Promise<void>[] = [];
                if (hydrate) {
                    hydratationPromises.push(this.damageValuesDbDao.getByCategoryId(damageCategory.id)
                        .then(possibleValues => {
                            possibleValues.forEach(possibleValue => {
                                damageCategory.possibleValues.push(possibleValue);
                            });
                        }));
                }
                return Promise.all(hydratationPromises)
                    .then(() => damageCategory);
            })
            .catch(reason => {
                this.logSqlError(reason);
                return null;
            });
    }

    public getTableName(): string {
        return DamageCategory.TABLENAME;
    }

    protected rowToModel(row: any): DamageCategory {
        const damageCategory = new DamageCategory();
        damageCategory.id = row.id;
        damageCategory.wording = row.wording;
        return damageCategory;
    }

    public save(damageCategory: DamageCategory): Promise<DamageCategory> {
        const savePromises = damageCategory.possibleValues.map(possibleValue =>
            this.damageValuesDbDao.save(possibleValue),
        );
        return Promise.all(savePromises).then(() => {
            const query = `INSERT OR REPLACE INTO ${ DamageCategory.TABLENAME } (id, wording) VALUES (?, ?);`;
            const params = [damageCategory.id, damageCategory.wording];
            return this.sqlProvider.query(query, params)
                .then(() => damageCategory)
                .catch(reason => {
                    this.logSqlError(reason);
                    return null;
                });
        });
    }

    private startListQuery(query: string, hydrate: boolean): Promise<DamageCategory[]> {
        return this.sqlProvider.query(query, [])
            .then(data => {
                if (data.rows.length <= 0) {
                    return [];
                }
                const hydratationPromises: Promise<void>[] = [];
                const damageCategories: DamageCategory[] = [];
                for (const item of data.rows) {
                    const damageCategory = this.rowToModel(item);
                    if (hydrate) {
                        hydratationPromises.push(this.damageValuesDbDao.getByCategoryId(damageCategory.id)
                            .then(possibleValues => {
                                possibleValues.forEach(possibleValue => {
                                    damageCategory.possibleValues.push(possibleValue);
                                });
                            }));
                    }
                    damageCategories.push(damageCategory);
                }
                return Promise.all(hydratationPromises)
                    .then(() => damageCategories);
            })
            .catch(reason => {
                this.logSqlError(reason);
                return null;
            });
    }
}
